import { get, set, del } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

type PersistAwareQuery = {
  meta?: {
    persist?: boolean;
  };
};

type PersistAwareMutation = {
  meta?: {
    persist?: boolean;
  };
};

/**
 * Creates an IndexedDB persister for React Query
 * 
 * IndexedDB provides:
 * - Much larger storage capacity (GBs vs 5-10MB for localStorage)
 * - Stores native JavaScript types (Date, File, etc.) without serialization
 * - Faster for large datasets
 * - Asynchronous (non-blocking)
 * 
 * Uses JSON serialization to ensure all data is cloneable and can be stored in IndexedDB.
 * This prevents DataCloneError when trying to store Promises or other non-cloneable objects.
 * 
 * @param idbValidKey - The key to use for storing the cache in IndexedDB
 * @returns A persister object that can be used with PersistQueryClientProvider
 * 
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */
export function createIDBPersister(idbValidKey: IDBValidKey = 'reactQuery'): Persister & { _disabled?: boolean } {
  const persister: Persister & { _disabled?: boolean } = {
    _disabled: false,
    persistClient: async (client: PersistedClient) => {
      // If persister is disabled, skip persistence
      // This is used during logout to prevent new queries from being persisted
      if (persister._disabled) {
        return;
      }
      
      try {
        // Serialize to JSON and back to ensure data is cloneable
        // This prevents DataCloneError from Promises or other non-serializable objects
        const serialized = JSON.stringify(client);
        const parsed = JSON.parse(serialized);
        await set(idbValidKey, parsed);
      } catch (error) {
        // Silently fail if serialization fails
        // This prevents breaking the app when trying to persist non-serializable data
        console.warn('Failed to persist query client:', error);
      }
    },
    restoreClient: async () => {
      try {
        const data = await get<PersistedClient>(idbValidKey);
        
        // Validate the restored data structure
        if (data && typeof data === 'object' && 'clientState' in data) {
          return data;
        }
        
        // If data is invalid, clear it and return undefined
        await del(idbValidKey);
        return undefined;
      } catch (error) {
        console.warn('Failed to restore query client:', error);
        // Clear corrupted data
        try {
          await del(idbValidKey);
        } catch {
          // Ignore cleanup errors
        }
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        await del(idbValidKey);
      } catch (error) {
        console.warn('Failed to remove query client:', error);
      }
    },
  };

  return persister;
}

/**
 * Creates a browser localStorage persister for React Query
 */
export function createLocalStoragePersister(storageKey = 'reactQuery'): Persister {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return {
      persistClient: async () => undefined,
      restoreClient: async () => undefined,
      removeClient: async () => undefined,
    };
  }

  return {
    persistClient: async (client: PersistedClient) => {
      try {
        const serialized = JSON.stringify(client);
        window.localStorage.setItem(storageKey, serialized);
      } catch (error) {
        console.warn('Failed to persist query client to localStorage:', error);
      }
    },
    restoreClient: async () => {
      try {
        const cache = window.localStorage.getItem(storageKey);
        if (!cache) {
          return undefined;
        }

        const parsed = JSON.parse(cache) as PersistedClient | undefined;

        if (parsed && typeof parsed === 'object' && 'clientState' in parsed) {
          return parsed;
        }

        window.localStorage.removeItem(storageKey);
        return undefined;
      } catch (error) {
        console.warn('Failed to restore query client from localStorage:', error);
        try {
          window.localStorage.removeItem(storageKey);
        } catch {
          // Ignore cleanup errors
        }
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (error) {
        console.warn('Failed to remove query client from localStorage:', error);
      }
    },
  };
}

/**
 * Pre-configured persister instance for the application
 */
const STORAGE_KEY = 'easydo-query-cache';
const persistenceMode = (import.meta.env.VITE_QUERY_PERSIST ?? 'idb').toLowerCase();

const selectedPersister =
  persistenceMode === 'local'
    ? createLocalStoragePersister(STORAGE_KEY)
    : createIDBPersister(STORAGE_KEY);

export const queryPersister: Persister = selectedPersister;

/**
 * Persistence configuration options
 */
export const persistOptions = {
  persister: queryPersister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  buster: '', // Change this string to invalidate all cached data
  
  dehydrateOptions: {
    // Selectively persist queries based on their meta.persist flag
    shouldDehydrateQuery: (query: PersistAwareQuery) => {
      // Skip persistence if meta.persist === false
      // By default (if meta.persist is not set), queries ARE persisted
      return query.meta?.persist !== false;
    },
    
    // Don't persist mutations by default (they're usually one-time operations)
    shouldDehydrateMutation: (mutation: PersistAwareMutation) => {
      // Only persist if explicitly enabled with meta.persist === true
      return mutation.meta?.persist === true;
    },
  },
};

